import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type {
  DeferredGroup,
  SeparationCounts,
  SeparationQuestion,
  SeparationStatus,
} from './separation.types'

interface SeparationState {
  /** Null until the first fetch resolves, which is what the screens wait on. */
  questions: SeparationQuestion[] | null
  sortedCount: number
  counts: SeparationCounts
  deferred: DeferredGroup[]
  status: SeparationStatus
  /** Set once the empty queue has been released, so a refetch cannot re-release it. */
  released: boolean
  /**
   * A load that failed rather than one still running. The screens wait on `status`, which a
   * failure leaves at `pending` forever, so without this they wait for something that is
   * never coming and the poll keeps hitting an endpoint that is not answering.
   */
  loadFailed: boolean
}

export const EMPTY_SEPARATION_COUNTS: SeparationCounts = {
  business: 0,
  personal: 0,
  setAside: 0,
  unsure: 0,
  movementPending: 0,
  likelyBusiness: 0,
  likelyPersonal: 0,
  leftForBookkeeper: 0,
  unclear: 0,
  unresolved: 0,
}

const initialState: SeparationState = {
  questions: null,
  sortedCount: 0,
  counts: EMPTY_SEPARATION_COUNTS,
  deferred: [],
  status: 'pending',
  released: false,
  loadFailed: false,
}

const separationSlice = createSlice({
  name: 'separation',
  initialState,
  reducers: {
    setSeparationQuestionSet: (
      state,
      action: PayloadAction<Omit<SeparationState, 'released' | 'loadFailed'>>
    ) => {
      Object.assign(state, action.payload)
      state.loadFailed = false
    },
    setSeparationLoadFailed: (state) => {
      state.questions = []
      state.loadFailed = true
    },
    markSeparationReleased: (state) => {
      state.released = true
    },
  },
})

export const {
  setSeparationQuestionSet,
  setSeparationLoadFailed,
  markSeparationReleased,
} = separationSlice.actions

export default separationSlice.reducer
